import { parseToDate } from './dates.utils';
import { TimeFormat } from '../enums/time-format.enum';
import dayjs from 'dayjs';

/**
 * Type representing time in HH:MM:SS format
 * Used for type-safe time strings from backend
 * @example '16:50:00', '09:30:15', '23:59:59'
 */
export type TimeString =
  `${number}${number}:${number}${number}:${number}${number}`;

/**
 * Validates if a string is in valid HH:MM:SS format
 * @param timeString - String to validate
 * @returns true if valid time format
 * @example
 * ```ts
 * isValidTimeString('16:50:00') // true
 * isValidTimeString('25:00:00') // false
 * isValidTimeString('9:30:00')  // false (missing leading zero)
 * ```
 */
export function isValidTimeString(
  timeString: string
): timeString is TimeString {
  const regex = /^([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
  return regex.test(timeString);
}

/**
 * Asserts that a string is a valid TimeString
 * Useful for runtime validation of backend responses
 * @param timeString - String to validate
 * @throws Error if string is not a valid time format
 * @example
 * ```ts
 * const backendTime = '16:50:00';
 * assertTimeString(backendTime);
 * // Now TypeScript knows backendTime is TimeString
 * ```
 */
export function assertTimeString(
  timeString: string
): asserts timeString is TimeString {
  if (!isValidTimeString(timeString)) {
    throw new Error(
      `Invalid time string format: ${timeString}. Expected HH:MM:SS`
    );
  }
}

/**
 * Parses a TimeString and extracts hours, minutes, and seconds
 * @param timeString - Valid time string in HH:MM:SS format
 * @returns Object with hours, minutes, seconds
 * @example
 * ```ts
 * parseTimeString('16:50:30') // { hours: 16, minutes: 50, seconds: 30 }
 * ```
 */
export function parseTimeString(timeString: TimeString | string): {
  hours: number;
  minutes: number;
  seconds: number;
} {
  const [hours = 0, minutes = 0, seconds = 0] = timeString
    .split(':')
    .map(Number);

  return { hours, minutes, seconds };
}

/**
 * Converts hours, minutes, and seconds to a TimeString
 * @param hours - Hour value (0-23)
 * @param minutes - Minute value (0-59)
 * @param seconds - Second value (0-59)
 * @returns Formatted time string or null if invalid
 * @example
 * ```ts
 * formatTimeString(16, 50, 0) // '16:50:00'
 * formatTimeString(25, 0, 0)  // null (invalid hour)
 * ```
 */
export function formatTimeString(
  hours: number,
  minutes: number,
  seconds: number
): TimeString | null {
  if (
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return null;
  }

  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` as TimeString;
}

/**
 * Compares two TimeStrings
 * @param time1 - First time string
 * @param time2 - Second time string
 * @returns -1 if time1 < time2, 0 if equal, 1 if time1 > time2
 * @example
 * ```ts
 * compareTimeStrings('09:00:00', '17:00:00') // -1
 * compareTimeStrings('16:50:00', '16:50:00') // 0
 * compareTimeStrings('23:00:00', '09:00:00') // 1
 * ```
 */
export function compareTimeStrings(
  time1: TimeString | string,
  time2: TimeString | string
): number {
  const t1 = parseTimeString(time1);
  const t2 = parseTimeString(time2);

  const seconds1 = t1.hours * 3600 + t1.minutes * 60 + t1.seconds;
  const seconds2 = t2.hours * 3600 + t2.minutes * 60 + t2.seconds;

  if (seconds1 < seconds2) return -1;
  if (seconds1 > seconds2) return 1;
  return 0;
}

/**
 * Checks if time1 is before time2
 * @param time1 - First time string
 * @param time2 - Second time string
 * @example
 * ```ts
 * isTimeBefore('09:00:00', '17:00:00') // true
 * isTimeBefore('17:00:00', '09:00:00') // false
 * ```
 */
export function isTimeBefore(time1: TimeString, time2: TimeString): boolean {
  return compareTimeStrings(time1, time2) < 0;
}

/**
 * Checks if time1 is after time2
 * @param time1 - First time string
 * @param time2 - Second time string
 * @example
 * ```ts
 * isTimeAfter('17:00:00', '09:00:00') // true
 * isTimeAfter('09:00:00', '17:00:00') // false
 * ```
 */
export function isTimeAfter(time1: TimeString, time2: TimeString): boolean {
  return compareTimeStrings(time1, time2) > 0;
}

/**
 * Type representing time in HH:mm format (UI / picker format, no seconds)
 * @example '14:30', '09:00', '00:00', '23:59'
 */
export type ShortTimeString = `${number}${number}:${number}${number}`;

/**
 * Validates if a string is in valid HH:mm format (hours 00-23, minutes 00-59)
 */
export function isValidShortTimeString(
  value: string
): value is ShortTimeString {
  return /^([0-1]\d|2[0-3]):[0-5]\d$/.test(value);
}

/**
 * Parses a ShortTimeString into hours and minutes
 */
export function parseShortTimeString(value: ShortTimeString): {
  hours: number;
  minutes: number;
} {
  const [h, m] = value.split(':').map(Number);
  return { hours: h, minutes: m };
}

/**
 * Builds a ShortTimeString from numeric hours and minutes, or null if invalid
 */
export function buildShortTimeString(
  hours: number,
  minutes: number
): ShortTimeString | null {
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}` as ShortTimeString;
}

/**
 * Calculates the difference between two times in seconds
 * @param startTime - Start time string
 * @param endTime - End time string
 * @returns Difference in seconds (always positive)
 * @example
 * ```ts
 * getTimeDifferenceInSeconds('09:00:00', '17:00:00') // 28800 (8 hours)
 * ```
 */
export function getTimeDifferenceInSeconds(
  startTime: TimeString,
  endTime: TimeString
): number {
  const start = parseTimeString(startTime);
  const end = parseTimeString(endTime);

  const startSeconds = start.hours * 3600 + start.minutes * 60 + start.seconds;
  const endSeconds = end.hours * 3600 + end.minutes * 60 + end.seconds;

  return Math.abs(endSeconds - startSeconds);
}

/**
 * Formats time for display (removes seconds if they're zero)
 * @param timeString - Time string in HH:MM:SS format
 * @param includeSeconds - Whether to always include seconds (default: false)
 * @returns Formatted time string
 * @example
 * ```ts
 * formatTimeForDisplay('16:50:00') // '16:50'
 * formatTimeForDisplay('16:50:30') // '16:50:30'
 * formatTimeForDisplay('16:50:00', true) // '16:50:00'
 * ```
 */
export function formatTimeForDisplay(
  timeString: TimeString,
  includeSeconds = false
): string {
  const { hours, minutes, seconds } = parseTimeString(timeString);
  const pad = (n: number) => n.toString().padStart(2, '0');

  if (includeSeconds || seconds !== 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(hours)}:${pad(minutes)}`;
}

/**
 * Validates whether a given string represents a valid time or date-time value.
 *
 * This function checks if the input can be parsed by the JavaScript `Date` object
 * and whether it results in a valid time (not `Invalid Date`).
 *
 * It supports various time formats, such as:
 * - `08:24:18.023Z`
 * - `2025-11-11T08:24:18.023Z`
 * - `08:24:18`
 *
 * @param timeString - The time or date-time string to validate.
 * @returns `true` if the given value is a valid time/date representation, otherwise `false`.
 *
 * @example
 * ```ts
 * isValidTime('08:24:18.023Z'); // true
 * isValidTime('25:00:00');      // false
 * isValidTime('2025-11-11T08:24:18Z'); // true
 * ```
 */
export function isValidTime(timeString: string): boolean {
  if (typeof timeString !== 'string' || !timeString.trim()) {
    return false;
  }

  // Try to parse using the Date constructor
  const parsed = new Date(timeString);
  if (!isNaN(parsed.getTime())) {
    return true;
  }

  // If it’s a plain time (HH:mm or HH:mm:ss or HH:mm:ss.SSS)
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d)(\.\d{1,3})?)?Z?$/;
  return timeRegex.test(timeString);
}

/**
 * Converts two time values into a formatted range string.
 *
 * The input values can be `Date` objects, ISO strings, or any valid
 * JavaScript time representations.
 *
 * @param start - The starting time (Date, string, or timestamp).
 * @param end - The ending time (Date, string, or timestamp).
 * @param withColons - If `true`, returns format `'hh:mm-hh:mm'`; otherwise `'hhmm-hhmm'`.
 * @returns The formatted time range string, or `null` if either time is invalid.
 *
 * @example
 * ```ts
 * formatTimeRange('08:24:18.023Z', '10:05:00Z'); // "0824-1005"
 * formatTimeRange(new Date('2025-11-11T08:24:00Z'), new Date('2025-11-11T10:05:00Z'), true);
 * // → "08:24-10:05"
 * ```
 */
export function formatTimeRange(
  start: Date | string | number,
  end: Date | string | number,
  withColons: boolean = false
) {
  if (
    (typeof start === 'string' && !isValidTime(start as string)) ||
    (typeof end === 'string' && !isValidTime(end as string))
  ) {
    return null;
  }
  const s = parseToDate(start);
  const e = parseToDate(end);

  if (isNaN(s.getTime()) || isNaN(e.getTime())) {
    return null;
  }

  const pad = (n: number) => n.toString().padStart(2, '0');
  const fmt = (d: Date) => {
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return withColons ? `${hh}:${mm}` : `${hh}${mm}`;
  };

  if (withColons) {
    return `${fmt(s)} - ${fmt(e)}` as `${TimeFormat.HH_MM}-${TimeFormat.HH_MM}`;
  }
  return `${fmt(s)} - ${fmt(e)}` as `${TimeFormat.HHMM}-${TimeFormat.HHMM}`;
}

/**
 * Calculates the difference between two times in hours, minutes, and seconds.
 *
 * Accepts any valid JavaScript time representation (`Date`, ISO string, or timestamp).
 *
 * @param start - The starting time.
 * @param end - The ending time.
 * @returns An object containing the absolute difference as `{ hours, minutes, seconds }`,
 *          or `null` if the input values are invalid.
 *
 * @example
 * ```ts
 * getTimeDifference('08:00:00Z', '09:30:30Z');
 * // → { hours: 1, minutes: 30, seconds: 30 }
 * ```
 */
export function getTimeDifference(
  start: Date | string | number,
  end: Date | string | number
): { hours: number; minutes: number; seconds: number } | null {
  if (
    (typeof start === 'string' && !isValidTime(start as string)) ||
    (typeof end === 'string' && !isValidTime(end as string))
  ) {
    return null;
  }
  const s = new Date(start);
  const e = new Date(end);

  if (isNaN(s.getTime()) || isNaN(e.getTime())) {
    return null;
  }

  const diffMs = Math.abs(e.getTime() - s.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { hours, minutes, seconds };
}

/**
 * Converts a 24-hour time format to 12-hour format with AM/PM.
 *
 * @param time - The time string in 24-hour format (e.g., "19:00", "08:30").
 * @returns The formatted time string in 12-hour format with AM/PM (e.g., "7:00 PM", "8:30 AM").
 *
 * @example
 * ```ts
 * formatTimeTo12Hour('19:00'); // "7:00 PM"
 * formatTimeTo12Hour('08:30'); // "8:30 AM"
 * formatTimeTo12Hour('00:00'); // "12:00 AM"
 * formatTimeTo12Hour('12:00'); // "12:00 PM"
 * ```
 */
export function formatTimeTo12Hour(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

export function toHHmmss(input: string | null | undefined): string {
  if (!input || input.trim() === '') return '00:00:00';

  if (!input.includes(':')) {
    const h = Number(input);
    if (isNaN(h)) return '00:00:00';
    if (h >= 24) return '23:59:59';
    return `${String(h).padStart(2, '0')}:00:00`;
  }

  const parts = input.split(':').map(Number);

  let [h, m = 0, s = 0] = parts;

  if (h === 24) return '23:59:59';

  h = Math.min(h, 23);
  m = Math.min(m, 59);
  s = Math.min(s, 59);

  return [
    String(h).padStart(2, '0'),
    String(m).padStart(2, '0'),
    String(s).padStart(2, '0')
  ].join(':');
}

export function format24Hour(time: string): string {
  const { hours, minutes } = parseTimeString(time as TimeString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}`;
}

export function format12Hour(time: string): string {
  const { hours, minutes } = parseTimeString(time as TimeString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';

  // Convert 24h to 12h format
  let displayHours = hours % 12;
  if (displayHours === 0) displayHours = 12; // Midnight (00:xx) or Noon (12:xx) should show as 12

  return `${displayHours}:${pad(minutes)} ${period}`;
}

/**
 * Converts a Date object or ISO date-time string to a TimeString (HH:MM:SS).
 *
 * @param input - A Date object or ISO string containing time information.
 * @returns A TimeString in HH:MM:SS format.
 * @throws Error if the input cannot be converted to a valid time.
 *
 * @example
 * ```ts
 * dateToTimeString(new Date('2025-11-11T16:50:30Z')) // '16:50:30'
 * dateToTimeString('2025-11-11T08:24:18.023Z') // '08:24:18'
 * dateToTimeString('16:50:00') // '16:50:00'
 * dateToTimeString('invalid') // throws Error
 * ```
 */
export function dateToTimeString(input: Date | string): TimeString {
  let date: Date;

  if (typeof input === 'string') {
    // If it's already in HH:MM:SS format, validate and return
    if (isValidTimeString(input)) return input as TimeString;

    // Otherwise parse as date
    date = new Date(input);
  } else date = input;

  if (isNaN(date.getTime())) {
    throw new Error(
      `Invalid date/time value: ${input}. Cannot convert to TimeString.`
    );
  }

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const timeString = formatTimeString(hours, minutes, seconds);
  if (!timeString) {
    throw new Error(
      `Invalid time components (hours: ${hours}, minutes: ${minutes}, seconds: ${seconds}). Cannot convert to TimeString.`
    );
  }

  return timeString;
}

/**
 * Updates the hours and minutes of an ISO date string.
 * * @param {string} isoString - The original date in ISO format.
 * @param {string} timeString - The new time in "HH:mm:ss" format.
 * @returns {string} The updated ISO string.
 */
export function updateIsoTime(
  isoString: string,
  timeString: TimeString
): string {
  // Split the "HH:mm" string into parts
  const [hours, minutes] = timeString.split(':');

  // Parse the ISO string and set the new values
  return dayjs(isoString)
    .hour(parseInt(hours, 10))
    .minute(parseInt(minutes, 10))
    .second(0)
    .millisecond(0)
    .toISOString();
}

/**
 * Converts a time value (in various formats) to seconds since midnight.
 * Accepts HH:mm:ss, HH:mm, Date objects, or ISO strings.
 *
 * @param time - Time value: TimeString/ShortTimeString, Date, or ISO string
 * @returns Seconds since midnight (0-86399), or null if invalid
 * @throws Error if the input cannot be parsed as a valid time
 *
 * @example
 * ```ts
 * timeToSeconds('16:50:30') // 60630
 * timeToSeconds('16:50') // 60600
 * timeToSeconds(new Date('2025-11-11T16:50:30Z')) // 60630
 * timeToSeconds('2025-11-11T16:50:30Z') // 60630
 * ```
 */
export function timeToSeconds(time: string | Date | number): number {
  let date: Date;

  if (typeof time === 'string') {
    // Try parsing as TimeString (HH:mm:ss format)
    if (isValidTimeString(time)) {
      const { hours, minutes, seconds } = parseTimeString(time);
      return hours * 3600 + minutes * 60 + seconds;
    }

    // Try parsing as ShortTimeString (HH:mm format)
    if (isValidShortTimeString(time)) {
      const { hours, minutes } = parseShortTimeString(time);
      return hours * 3600 + minutes * 60;
    }

    // Try parsing as Date/ISO string
    date = parseToDate(time);
  } else if (typeof time === 'number') {
    date = new Date(time);
  } else if (time instanceof Date) {
    date = time;
  } else {
    throw new Error(`Invalid time format: ${time}`);
  }

  if (isNaN(date.getTime())) {
    throw new Error(`Cannot parse time value: ${time}`);
  }

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  return hours * 3600 + minutes * 60 + seconds;
}
